import * as React from "react"

import { ListPageLoadingFallback } from "@/components/templates/list-page-loading-fallback"

const PAGE_REVEAL_MS = 520
const TAB_REVEAL_MS = 420

/**
 * Route fallback. Uses the package hub skeleton (DS `Skeleton` bars).
 */
export function LoadingFallback() {
  return <ListPageLoadingFallback showMetrics tabCount={4} rowCount={6} />
}

/**
 * Tab-body fallback. Same package skeleton, without a second KPI band.
 */
export function TabLoadingFallback() {
  return (
    <ListPageLoadingFallback
      showMetrics={false}
      tabCount={3}
      rowCount={6}
      className="pt-1"
    />
  )
}

/** First paint stays put. Later key changes hold a skeleton for `ms`. */
export function useRevealAfter(key: string, ms = TAB_REVEAL_MS) {
  const [ready, setReady] = React.useState(true)
  // The key last shown. A first-run flag would not do: Strict Mode runs the
  // effect twice on mount, and the second run hid the tab for nothing,
  // unmounting whatever was mid-work inside it (Leo assembling a paper).
  const shownRef = React.useRef(key)

  React.useEffect(() => {
    if (shownRef.current === key) return
    shownRef.current = key
    setReady(false)
    const timer = window.setTimeout(() => setReady(true), ms)
    return () => window.clearTimeout(timer)
  }, [key, ms])

  return ready
}

/**
 * First paint of a focus page (compose, import review). Tests skip the hold
 * so they see the real surface without advancing timers.
 */
export function usePageReveal(ms = PAGE_REVEAL_MS) {
  const hold = import.meta.env.MODE === "test" ? 0 : ms
  const [ready, setReady] = React.useState(hold <= 0)

  React.useEffect(() => {
    if (hold <= 0) return
    const timer = window.setTimeout(() => setReady(true), hold)
    return () => window.clearTimeout(timer)
  }, [hold])

  return ready
}

export { PAGE_REVEAL_MS, TAB_REVEAL_MS }
